import {Billing, Charts, StationMeta, applySensorConfig} from '@smartmixin/core';
import {messages} from '@smartmixin/i18n';
import {
  Blur,
  ChartLine,
  Flash,
  Leaf,
  MoleculeCo2,
  Thermometer,
  WaterPercent,
} from 'mdi-material-ui';
import React from 'react';
import {MessageDescriptor} from 'react-intl';

export type SensorType =
  | 'temperature'
  | 'soil'
  | 'leaf'
  | 'leafDavis'
  | 'pm25'
  | 'indoor'
  | 'indoorCombo'
  | 'bgt'
  | 'lightning';

export type VisualizationDef = {
  id: string;
  titleMessage: MessageDescriptor;
  chartType: string;
  premiumPlan: number;
  icon: React.ReactNode;
  /** Metric suffix to filter channels — e.g. 'T' matches keys ending in '_T' */
  metricSuffix?: string;
  /** Secondary metric suffix for dual-axis charts — e.g. 'U' for humidity on right axis */
  secondaryMetricSuffix?: string;
  /** Explicit primary metric keys — overrides suffix-based computation */
  metricKeys?: string[];
  /** Explicit secondary metric keys for right axis — overrides suffix-based computation */
  secondaryMetricKeys?: string[];
  /** When true, secondary metrics share the primary Y-axis instead of a separate right axis */
  sameAxis?: boolean;
  /** Secondary metrics that share the primary Y-axis; others get a separate right axis */
  sameAxisMetricKeys?: string[];
  /** Per-metric color overrides — values are {light, dark} pairs */
  metricColors?: Record<string, {light: string; dark: string}>;
  /** Custom series names keyed by metric key — overrides default metric-type labels in tooltips/legend */
  metricLabels?: Record<string, MessageDescriptor>;
  /** Series type — 'line' (default) or 'bar' for bar charts */
  seriesType?: 'line' | 'bar';
  /** When true, metricKeys are not filtered against last_measure (for history-only metrics) */
  historyOnlyMetrics?: boolean;
  /** Min/max range metrics to fetch at aggregated granularities, keyed by the average metric */
  rangeMetrics?: Record<string, {min: string; max: string}>;
};

export type SensorTypeConfig = {
  slug: string;
  titleMessage: MessageDescriptor;
  channelKeys: string[];
  /** Whether channelKeys are prefixes (true) or exact keys (false) */
  prefixMatch: boolean;
  visualizations: VisualizationDef[];
};

const TEMP_PREFIXES = [
  'TEMP_CH1_',
  'TEMP_CH2_',
  'TEMP_CH3_',
  'TEMP_CH4_',
  'TEMP_CH5_',
  'TEMP_CH6_',
  'TEMP_CH7_',
  'TEMP_CH8_',
];

const TEMP_AND_HUMIDITY_PREFIXES = [
  'TEMP_AND_HUMIDITY_CH1_',
  'TEMP_AND_HUMIDITY_CH2_',
  'TEMP_AND_HUMIDITY_CH3_',
  'TEMP_AND_HUMIDITY_CH4_',
  'TEMP_AND_HUMIDITY_CH5_',
  'TEMP_AND_HUMIDITY_CH6_',
  'TEMP_AND_HUMIDITY_CH7_',
  'TEMP_AND_HUMIDITY_CH8_',
];

const SOIL_PREFIXES = Array.from({length: 16}, (_, i) => `SOIL_CH${i + 1}_`);

const LEAF_CHANNELS = [
  'LEAF_WETNESS_CH1',
  'LEAF_WETNESS_CH2',
  'LEAF_WETNESS_CH3',
  'LEAF_WETNESS_CH4',
  'LEAF_WETNESS_CH5',
  'LEAF_WETNESS_CH6',
  'LEAF_WETNESS_CH7',
  'LEAF_WETNESS_CH8',
];

const LEAF_DAVIS_CHANNELS = [
  'LEAF_WETNESS_DAVIS_CH1',
  'LEAF_WETNESS_DAVIS_CH2',
  'LEAF_WETNESS_DAVIS_CH3',
  'LEAF_WETNESS_DAVIS_CH4',
];

const PM25_PREFIXES = ['PM25_CH1_', 'PM25_CH2_', 'PM25_CH3_', 'PM25_CH4_'];

const INDOOR_CHANNELS = [
  'INDOOR_T',
  'INDOOR_U',
  'INDOOR_CO2',
  'INDOOR_DP',
  'INDOOR_FEELS_LIKE',
  'INDOOR_APP_TEMP',
];

const BGT_CHANNELS = ['BLACK_GLOBE_T', 'WET_BULB_GLOBE'];

const LIGHTNING_CHANNELS = ['LIGHTNING_STRIKE_COUNT_TODAY', 'LIGHTNING_STRIKE_COUNT_1H'];

const INDOOR_COMBO_CHANNELS = [
  'AQI_COMBO_PM25',
  'AQI_COMBO_PM25_AQI',
  'AQI_COMBO_PM25_AQI_24H',
  'AQI_COMBO_PM10',
  'AQI_COMBO_PM10_AQI',
  'AQI_COMBO_PM10_AQI_24H',
  'AQI_COMBO_PM1',
  'AQI_COMBO_PM1_AQI',
  'AQI_COMBO_PM1_AQI_24H',
  'AQI_COMBO_PM4',
  'AQI_COMBO_PM4_AQI',
  'AQI_COMBO_PM4_AQI_24H',
  'AQI_COMBO_CO2',
  'AQI_COMBO_T',
  'AQI_COMBO_U',
];

const sensorConfig: Record<SensorType, SensorTypeConfig> = {
  temperature: {
    slug: 'temperature',
    titleMessage: messages.units.temperatureSensors,
    channelKeys: [...TEMP_PREFIXES, ...TEMP_AND_HUMIDITY_PREFIXES],
    prefixMatch: true,
    visualizations: [
      {
        id: 'temp_individual',
        titleMessage: messages.sensors.temperatureByChannel,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <Thermometer />,
        metricSuffix: 'T',
        secondaryMetricSuffix: 'U',
      },
      {
        id: 'temp_compare',
        titleMessage: messages.sensors.compareTemperatures,
        chartType: 'compareTemperature',
        premiumPlan: Billing.PREMIUM,
        icon: <ChartLine />,
        metricSuffix: 'T',
      },
      {
        id: 'th_compare_humidity',
        titleMessage: messages.sensors.compareHumidity,
        chartType: 'compareHumidity',
        premiumPlan: Billing.PREMIUM,
        icon: <ChartLine />,
        metricSuffix: 'U',
      },
    ],
  },
  soil: {
    slug: 'soil',
    titleMessage: messages.units.soilSensors,
    channelKeys: SOIL_PREFIXES,
    prefixMatch: true,
    visualizations: [
      {
        id: 'soil_moisture_individual',
        titleMessage: messages.sensors.moistureByChannel,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <WaterPercent />,
        metricSuffix: 'U',
      },
      {
        id: 'soil_temp_individual',
        titleMessage: messages.sensors.soilTemperatureByChannel,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <Thermometer />,
        metricSuffix: 'T',
      },
      {
        id: 'soil_compare_moisture',
        titleMessage: messages.sensors.compareMoisture,
        chartType: 'compareMoisture',
        premiumPlan: Billing.PREMIUM,
        icon: <ChartLine />,
        metricSuffix: 'U',
      },
    ],
  },
  leaf: {
    slug: 'leaf',
    titleMessage: messages.units.leafSensors,
    channelKeys: LEAF_CHANNELS,
    prefixMatch: false,
    visualizations: [
      {
        id: 'leaf_wetness_individual',
        titleMessage: messages.sensors.leafWetnessByChannel,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <Leaf />,
      },
    ],
  },
  leafDavis: {
    slug: 'leaf-davis',
    titleMessage: messages.units.leafSensors,
    channelKeys: LEAF_DAVIS_CHANNELS,
    prefixMatch: false,
    visualizations: [
      {
        id: 'leaf_davis_wetness_individual',
        titleMessage: messages.sensors.leafWetnessByChannel,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <Leaf />,
        secondaryMetricKeys: [
          'LEAF_TEMP_DAVIS_CH1',
          'LEAF_TEMP_DAVIS_CH2',
          'LEAF_TEMP_DAVIS_CH3',
          'LEAF_TEMP_DAVIS_CH4',
        ],
      },
    ],
  },
  pm25: {
    slug: 'pm25',
    titleMessage: messages.airQuality.airQuality,
    channelKeys: PM25_PREFIXES,
    prefixMatch: true,
    visualizations: [
      {
        id: 'pm25_individual',
        titleMessage: messages.sensors.pm25ByChannel,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <Blur />,
        metricSuffix: 'PM25',
        secondaryMetricSuffix: 'AQI',
      },
      {
        id: 'pm25_compare',
        titleMessage: messages.sensors.comparePm25,
        chartType: 'comparePm25',
        premiumPlan: Billing.PREMIUM,
        icon: <ChartLine />,
        metricSuffix: 'PM25',
      },
    ],
  },
  indoor: {
    slug: 'indoor',
    titleMessage: messages.common.indoor,
    channelKeys: INDOOR_CHANNELS,
    prefixMatch: false,
    visualizations: [
      {
        id: 'indoor_climate',
        titleMessage: messages.sensors.indoorClimate,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <Thermometer />,
        metricKeys: ['INDOOR_T'],
        secondaryMetricKeys: [
          'INDOOR_DP',
          'INDOOR_FEELS_LIKE',
          'INDOOR_APP_TEMP',
          'INDOOR_U',
        ],
        sameAxisMetricKeys: ['INDOOR_DP', 'INDOOR_FEELS_LIKE', 'INDOOR_APP_TEMP'],
        metricColors: {
          INDOOR_DP: Charts.METRIC_COLORS.dewPoint,
          INDOOR_FEELS_LIKE: Charts.METRIC_COLORS.feelsLike,
          INDOOR_APP_TEMP: Charts.METRIC_COLORS.apparentTemperature,
          INDOOR_U: Charts.METRIC_COLORS.humidity,
        },
        metricLabels: {
          INDOOR_T: messages.units.temperature,
          INDOOR_DP: messages.units.dewPoint,
          INDOOR_FEELS_LIKE: messages.units.feelLike,
          INDOOR_APP_TEMP: messages.units.apparentTemperature,
          INDOOR_U: messages.units.humidity,
        },
      },
      {
        id: 'indoor_co2',
        titleMessage: messages.sensors.indoorCo2Levels,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <MoleculeCo2 />,
        metricSuffix: 'CO2',
      },
    ],
  },
  indoorCombo: {
    slug: 'indoor-combo',
    titleMessage: messages.sensors.indoorAirQuality,
    channelKeys: INDOOR_COMBO_CHANNELS,
    prefixMatch: false,
    visualizations: [
      {
        id: 'combo_pm25',
        titleMessage: messages.sensors.pm25AndAqi,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <Blur />,
        metricKeys: ['AQI_COMBO_PM25'],
        secondaryMetricKeys: ['AQI_COMBO_PM25_AQI', 'AQI_COMBO_PM25_AQI_24H'],
      },
      {
        id: 'combo_pm10',
        titleMessage: messages.sensors.pm10AndAqi,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <Blur />,
        metricKeys: ['AQI_COMBO_PM10'],
        secondaryMetricKeys: ['AQI_COMBO_PM10_AQI', 'AQI_COMBO_PM10_AQI_24H'],
      },
      {
        id: 'combo_pm1',
        titleMessage: messages.sensors.pm1AndAqi,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <Blur />,
        metricKeys: ['AQI_COMBO_PM1'],
        secondaryMetricKeys: ['AQI_COMBO_PM1_AQI', 'AQI_COMBO_PM1_AQI_24H'],
      },
      {
        id: 'combo_pm4',
        titleMessage: messages.sensors.pm4AndAqi,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <Blur />,
        metricKeys: ['AQI_COMBO_PM4'],
        secondaryMetricKeys: ['AQI_COMBO_PM4_AQI', 'AQI_COMBO_PM4_AQI_24H'],
      },
      {
        id: 'combo_temp_humidity',
        titleMessage: messages.sensors.indoorTemperatureAndHumidity,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <Thermometer />,
        metricSuffix: 'T',
        secondaryMetricSuffix: 'U',
      },
      {
        id: 'combo_co2',
        titleMessage: messages.sensors.indoorCo2Levels,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <MoleculeCo2 />,
        metricSuffix: 'CO2',
      },
    ],
  },
  bgt: {
    slug: 'bgt',
    titleMessage: messages.sensors.bgtSensors,
    channelKeys: BGT_CHANNELS,
    prefixMatch: false,
    visualizations: [
      {
        id: 'bgt_individual',
        titleMessage: messages.sensors.bgtSensors,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <Thermometer />,
        metricKeys: ['BLACK_GLOBE_T'],
        secondaryMetricKeys: ['WET_BULB_GLOBE'],
        sameAxis: true,
        metricLabels: {
          BLACK_GLOBE_T: messages.units.blackGlobeTemperature,
          WET_BULB_GLOBE: messages.units.wetBulbGlobeTemperature,
        },
      },
    ],
  },
  lightning: {
    slug: 'lightning',
    titleMessage: messages.sensors.lightningSensor,
    channelKeys: LIGHTNING_CHANNELS,
    prefixMatch: false,
    visualizations: [
      {
        id: 'lightning_strike_count',
        titleMessage: messages.units.lightningStrikeCount,
        chartType: 'individual',
        premiumPlan: Billing.FREEMIUM,
        icon: <Flash />,
        metricKeys: ['LIGHTNING_STRIKE_COUNT'],
        secondaryMetricKeys: ['LIGHTNING_STRIKE_DISTANCE'],
        seriesType: 'bar',
        historyOnlyMetrics: true,
        metricLabels: {
          LIGHTNING_STRIKE_COUNT: messages.units.lightningStrikeCount,
          LIGHTNING_STRIKE_DISTANCE: messages.units.lightningStrikeDistance,
        },
        rangeMetrics: {
          LIGHTNING_STRIKE_DISTANCE: {
            min: 'LIGHTNING_STRIKE_DISTANCE_MIN',
            max: 'LIGHTNING_STRIKE_DISTANCE_MAX',
          },
        },
      },
    ],
  },
};

/**
 * Get the active channels for a sensor type from station.last_measure.
 */
export const getActiveChannels = (
  sensorType: SensorType,
  lastMeasure: Record<string, unknown>,
): string[] => {
  const config = sensorConfig[sensorType];
  const measureKeys = Object.keys(lastMeasure);

  if (config.prefixMatch) {
    return config.channelKeys.filter((prefix) =>
      measureKeys.some((key) => key.startsWith(prefix)),
    );
  }
  return config.channelKeys.filter((key) => measureKeys.includes(key));
};

/**
 * Get the metric keys to fetch for a visualization.
 * For prefix-matched sensors, appends the metricSuffix.
 * For exact-match sensors, returns the channel keys directly.
 * When meta is provided, applies sensor order/visibility config.
 */
export const getMetricKeysForVisualization = (
  sensorType: SensorType,
  visualization: VisualizationDef,
  lastMeasure: Record<string, unknown>,
  meta?: StationMeta,
): string[] => {
  if (visualization.metricKeys) {
    if (visualization.historyOnlyMetrics) {
      return visualization.metricKeys;
    }
    return visualization.metricKeys.filter((key) => key in lastMeasure);
  }

  let activeChannels = getActiveChannels(sensorType, lastMeasure);
  if (meta) {
    activeChannels = applySensorConfig(activeChannels, sensorType, meta);
  }
  const config = sensorConfig[sensorType];

  if (config.prefixMatch && visualization.metricSuffix) {
    return activeChannels
      .map((prefix) => `${prefix}${visualization.metricSuffix}`)
      .filter((key) => key in lastMeasure);
  }

  if (!config.prefixMatch && visualization.metricSuffix) {
    const mapped = activeChannels
      .map((key) => {
        const lastUnderscore = key.lastIndexOf('_');
        return `${key.substring(0, lastUnderscore + 1)}${visualization.metricSuffix}`;
      })
      .filter((key) => key in lastMeasure);
    return [...new Set(mapped)];
  }

  return activeChannels;
};

export const sensorTypeToSlug = (type: SensorType): string =>
  sensorConfig[type].slug;

export const slugToSensorType = (slug: string): SensorType | undefined => {
  // Legacy: temp-and-humidity slug maps to merged temperature type
  if (slug === 'temp-and-humidity') return 'temperature';
  return (Object.keys(sensorConfig) as SensorType[]).find(
    (key) => sensorConfig[key].slug === slug,
  );
};

export default sensorConfig;
